import React from 'react';
import { KenticoDataContext } from '../../../../../contexts/kentico-data-context';
import { ISplashContent } from '../../../banner';
import { assetNames } from '../asset-names';

interface ISplashActiveProps {
  /** Parsed splash content from the active outage's splash config. */
  content: Partial<ISplashContent>;
  /** Simple remote config, used for the registration redirect URL. */
  config: Record<string, string>;
}

const copyrightText = `© ${new Date().getFullYear()} Unum Group. All rights reserved. Insurance products are underwritten by the subsidiaries of Unum Group. NS-580009`;

// Reused from the landing's register action so the button looks identical.
const registerButtonClasses =
  'tw-font-sans tw-font-semibold tw-text-base tw-text-white tw-bg-[#017f90] tw-border tw-border-solid tw-border-[#017f90] tw-rounded-[30px] tw-min-w-[226px] tw-px-6 tw-py-3 tw-no-underline tw-whitespace-nowrap hover:tw-bg-[#0199a6] hover:tw-text-white hover:tw-no-underline';

/**
 * Full-page splash shown while an outage is active. Mirrors the prior site's
 * `Splash` component: the Unum logo, the outage messaging from the splash
 * config (with already-registered copy when arriving via the `?a`
 * querystring), an optional register action, the outage illustration, and the
 * copyright line. Styling is authored in Tailwind rather than styled-components.
 */
const SplashActive = (props: Readonly<ISplashActiveProps>) => {
  const { content, config } = props;
  const { assets } = React.useContext(KenticoDataContext);

  // Pressing Shift latches an invisible bypass link over the copyright icon
  // (see `isSplashBypassed`) so testers can click through to skip the splash.
  const [linkEnabled, setLinkEnabled] = React.useState(false);

  React.useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Shift') {
        setLinkEnabled(true);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // `/?a` is the path `/app` redirects to for already-registered claimants.
  const isAlreadyRegistered = new URLSearchParams(window.location.search).has(
    'a',
  );

  const paragraph = isAlreadyRegistered
    ? content.paragraphAlreadyRegistered
    : content.paragraph;

  return (
    <div
      id="splash-active"
      className="tw-box-border tw-flex tw-h-full tw-flex-col tw-justify-between tw-items-center tw-bg-unm-container-bg tw-p-8 min-[765px]:tw-p-[60px] min-[835px]:tw-px-[140px] min-[835px]:tw-py-[52px]"
    >
      <img
        id="splash-unum-logo"
        className="tw-self-start"
        src={assets[assetNames.logo]?.url}
        alt=""
      />
      <div className="tw-flex tw-flex-col-reverse tw-items-center min-[980px]:tw-flex-row">
        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center min-[980px]:tw-items-start">
          <h1
            id="splash-title"
            className="tw-font-sans tw-font-light tw-text-[44px] tw-text-[#335E7A] tw-text-center min-[980px]:tw-text-left"
          >
            {content.header}
          </h1>
          <p
            id="splash-paragraph"
            className="tw-font-sans tw-font-normal tw-text-base tw-text-[#2e2e2e] tw-max-w-[500px] tw-text-center min-[980px]:tw-text-left"
          >
            {paragraph}
          </p>
          {!isAlreadyRegistered && (
            <a
              id="splash-registration-button"
              className={`tw-mt-4 ${registerButtonClasses}`}
              href={config['registration_redirect_url']}
              rel="noopener"
            >
              Register for an account
            </a>
          )}
        </div>
        <img
          id="landing-outage-image"
          src={assets[assetNames.outage]?.url}
          alt="The portal is temporarily unavailable for scheduled maintenance."
          className="tw-mb-4 tw-max-w-full"
        />
      </div>
      <div id="splash-copyright" className="tw-flex tw-items-end tw-self-start">
        <div className="tw-relative tw-min-w-[40px] tw-h-10 tw-mr-[15px]">
          <div
            id="splash-copyright-image"
            className="tw-w-full tw-h-full tw-bg-center tw-bg-no-repeat"
            style={{
              backgroundImage: assets[assetNames.unumIcon]?.url
                ? `url(${assets[assetNames.unumIcon]?.url})`
                : undefined,
            }}
          />
          {linkEnabled && (
            <div className="tw-absolute tw-inset-0 tw-flex">
              <a
                id="splash-login-link"
                href={config['login_redirect_url']}
                aria-label="Log in to the portal"
                className="tw-flex-1 tw-h-full tw-no-underline"
              >
                &nbsp;
              </a>
              <a
                id="splash-bypass-link"
                href="/app?splashbypass"
                aria-label="Bypass the outage splash"
                className="tw-flex-1 tw-h-full tw-no-underline"
              >
                &nbsp;
              </a>
            </div>
          )}
        </div>
        <p
          id="splash-copyright-text"
          className="tw-font-sans tw-font-normal tw-text-sm tw-m-0 tw-max-w-[500px] tw-text-[#2e2e2e]"
        >
          {copyrightText}
        </p>
      </div>
    </div>
  );
};

export { SplashActive };
